class LilLuxeCategoryCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.toggleButton = this.querySelector('[data-carousel-toggle]');
    this.pauseIcon = this.querySelector('[data-carousel-pause-icon]');
    this.playIcon = this.querySelector('[data-carousel-play-icon]');
    this.status = this.querySelector('[data-carousel-status]');
    this.progress = this.querySelector('[data-carousel-progress]');

    if (!this.track || this.slides.length < 2) return;

    this.abortController = new AbortController();
    this.pauseReasons = new Set();
    this.currentIndex = 0;
    this.manualPaused = false;
    this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
    this.autoplayRequested = this.dataset.autoplay === 'true';
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.move(-1, true), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1, true), { signal });
    this.toggleButton?.addEventListener('click', this.toggleAutoplay, { signal });
    this.track.addEventListener('scroll', this.handleScroll, { signal, passive: true });
    this.addEventListener('pointerenter', () => this.pause('pointer'), { signal });
    this.addEventListener('pointerleave', () => this.resume('pointer'), { signal });
    this.addEventListener('focusin', () => this.pause('focus'), { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal });
    this.reducedMotionQuery.addEventListener('change', this.handleReducedMotion, { signal });

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.track);
    this.classList.add('is-ready');
    this.updateAutoplayUI();
    this.updateOverflow();
  }

  disconnectedCallback() {
    this.clearTimer();
    window.clearTimeout(this.scrollEndTimer);
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
  }

  get autoplayAvailable() {
    return this.autoplayRequested && !this.reducedMotionQuery.matches && !this.classList.contains('no-overflow');
  }

  get canRunTimer() {
    return this.autoplayAvailable && !this.manualPaused && this.pauseReasons.size === 0;
  }

  get visibleSlides() {
    return this.slides.filter((slide) => window.getComputedStyle(slide).display !== 'none');
  }

  move(direction, announce = false) {
    const slides = this.visibleSlides;
    if (slides.length < 2) return;
    const targetIndex = (this.currentIndex + direction + slides.length) % slides.length;
    this.scrollToSlide(targetIndex, announce);
  }

  scrollToSlide(index, announce = false) {
    const slides = this.visibleSlides;
    const target = slides[index];
    if (!target) return;

    this.currentIndex = index;
    this.track.scrollTo({
      left: this.getSlideLeft(target),
      behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
    });

    if (announce && this.status) {
      const label = target.dataset.slideLabel || `Gift type ${index + 1}`;
      this.status.textContent = `Showing ${label}, ${index + 1} of ${slides.length}`;
    }
    this.startTimer();
  }

  handleScroll = () => {
    this.clearTimer();
    window.clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = window.setTimeout(() => {
      this.currentIndex = this.getClosestSlideIndex();
      this.startTimer();
    }, 140);
  };

  getClosestSlideIndex() {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.visibleSlides.forEach((slide, index) => {
      const distance = Math.abs(this.getSlideLeft(slide) - this.track.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  getSlideLeft(slide) {
    return slide.offsetLeft - this.track.offsetLeft;
  }

  toggleAutoplay = () => {
    this.manualPaused = !this.manualPaused;
    this.updateAutoplayUI();
    this.startTimer();
  };

  updateAutoplayUI() {
    const unavailable = !this.autoplayAvailable;
    this.classList.toggle('no-autoplay', unavailable);
    if (!this.toggleButton) return;

    const paused = unavailable || this.manualPaused;
    this.toggleButton.setAttribute('aria-pressed', String(this.manualPaused));
    this.toggleButton.setAttribute('aria-label', paused ? 'Start automatic rotation' : 'Pause automatic rotation');
    if (this.pauseIcon) this.pauseIcon.hidden = paused;
    if (this.playIcon) this.playIcon.hidden = !paused;
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    this.clearTimer();
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    this.startTimer();
  }

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.resume('focus');
  };

  handleVisibility = () => {
    if (document.hidden) this.pause('document');
    else this.resume('document');
  };

  handleReducedMotion = () => {
    this.updateAutoplayUI();
    this.startTimer();
  };

  handleResize = () => {
    const slides = this.visibleSlides;
    this.currentIndex = Math.min(this.currentIndex, Math.max(slides.length - 1, 0));
    this.updateOverflow();
    this.scrollToSlide(this.currentIndex);
  };

  updateOverflow() {
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
    this.classList.toggle('no-overflow', !hasOverflow);
    this.updateAutoplayUI();
    this.startTimer();
  }

  startTimer() {
    this.clearTimer();
    if (!this.canRunTimer) return;

    void this.progress?.getBoundingClientRect();
    this.classList.add('is-timing');
    this.autoplayTimer = window.setTimeout(() => this.move(1), this.interval);
  }

  clearTimer() {
    window.clearTimeout(this.autoplayTimer);
    this.classList.remove('is-timing');
  }
}

if (!customElements.get('ll-category-carousel')) {
  customElements.define('ll-category-carousel', LilLuxeCategoryCarousel);
}
